/**
 * getRollbarContext
 *
 * Build context data for Rollbar error reporting
 */
import store from '~/store'
import { selectCredits, selectSession, selectSessionId } from '~/store/api/selectors'
import { selectAblyState, selectIsActive, selectProjectId, selectRTCState } from '~/store/app/selectors'
import {
	APP_ENV,
	APP_VERSION,
	BROWSER_NAME,
	COMMIT_HASH,
	COMMIT_NAME,
	DEVICE_ID,
	DEVICE_NAME,
	IS_MOBILE,
	IS_OUTPUT,
	RTC_DIRECTION,
} from './constants'
import { getLatestStats } from './statsLog'

/**
 * Get context data for error reports (Rollbar, bug reports, etc.)
 * @param {Object} options - Options for context building
 * @param {boolean} options.includeVerbose - Include verbose data (for manual bug reports)
 * @returns {Object} Context object with session, environment, and connection state
 */
const getRollbarContext = ({ includeVerbose = false } = {}) => {
	const state = store.getState()
	const { app, camera, live, editor, playback, ui } = state
	const session = selectSession(state)
	const credits = selectCredits(state)

	// Base context (always included)
	const context = {
		// Session
		session_id: selectSessionId(state),
		session_status: session?.status,

		// Environment
		user_agent: navigator.userAgent,
		platform: navigator.platform,
		app_version: APP_VERSION,
		app_env: APP_ENV,
		commit_hash: COMMIT_HASH,
		commit_name: COMMIT_NAME,
		device_id: DEVICE_ID,
		device_name: DEVICE_NAME,
		browser_name: BROWSER_NAME,

		// Connection state
		rtc_state: selectRTCState(state),
		rtc_direction: RTC_DIRECTION,
		ably_state: selectAblyState(state),
		is_active: selectIsActive(state),

		// App context
		project_id: selectProjectId(state),
		is_recording: app.recording?.is_recording || false,
		credits: credits?.amount || 0,
		camera: camera.device,
		presence: app.presence,
		session_presence: app.session_presence,
		rtc_stats: getLatestStats(),
		device_type: {
			is_mobile: IS_MOBILE,
			client_mode: app.client_mode,
			is_output: IS_OUTPUT,
		},
	}

	// Verbose data (only for manual bug reports via Feedback form)
	if (includeVerbose) {
		const { rtc_state, ably_state, presence, recording, project_id, settings, prompt, active_device } = app
		const { confirm, dialog, pending, show } = ui

		Object.assign(context, {
			// Additional state for debugging
			ably_state,
			active_device,
			camera_settings: camera.camera_settings,
			cameras: camera.available,
			confirm,
			credits,
			device_id: DEVICE_ID,
			dialog,
			editor,
			is_active: selectIsActive(state),
			live,
			pending,
			playback,
			presence,
			project_id,
			prompt,
			recording,
			rtc_state,
			selection: state.selection,
			session,
			settings,
			show,
			window_size: `${window.innerWidth}x${window.innerHeight}`,
		})
	}

	return context
}

/**
 * Get WebRTC stats if available
 * @returns {Promise<Object>} WebRTC stats or empty object
 */
export const getWebRTCStats = async () => {
	// Use window.pc instead of importing socket to avoid circular dependency
	if (!window.pc) return {}

	try {
		const stats = await window.pc.getStats()
		const result = {}
		stats.forEach(report => {
			if (report?.type === 'inbound-rtp') {
				result.in_fps = report.framesPerSecond
			} else if (report?.type === 'outbound-rtp') {
				result.out_fps = report.framesPerSecond
			}
		})
		return result
	} catch (err) {
		console.error('[getRollbarContext] Failed to get WebRTC stats:', err)
		return {}
	}
}

export default getRollbarContext
